import { Badge as UiBadge } from '@Pages/pageRuntime/components/ui/Badge';
import { Button as UiButton } from '@Pages/pageRuntime/components/ui/Button';
import { SearchBar as UiSearchBar } from '@Pages/pageRuntime/components/ui/SearchBar';
import { Separator as UiSeparator } from '@Pages/pageRuntime/components/ui/Separator';
import { Skeleton as UiSkeleton } from '@Pages/pageRuntime/components/ui/Skeleton';
import { Spinner as UiSpinner } from '@Pages/pageRuntime/components/ui/Spinner';

import { Accordion } from './Accordion';
import { AdminNavButtons } from './AdminNavButtons';
import { Alert } from './Alert';
import { AnalyticsEmbed } from './AnalyticsEmbed';
import { Avatar } from './Avatar';
import { Card } from './Card';
import { Collapsible } from './Collapsible';
import { ConfirmDialog } from './ConfirmDialog';
import { List } from './DataViews';
import { DeleteResourceRecord } from './DeleteResourceRecord';
import { EmptyState } from './EmptyState';
import { ErrorBoundary } from './ErrorBoundary';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { HasAnyPermission, HasPermission } from './HasPermission';
import { KeyValueList } from './KeyValueList';
import { BarChart, LineChart, PieChart } from './LazyCharts';
import { lazyComponent } from './lazyComponent';
import { LoginForm } from './LoginForm';
import { Modal } from './Modal';
import { Breadcrumb, ButtonLink, DropdownMenu, Icon, Link } from './Navigation';
import { PageHeader } from './PageHeader';
import { Popover } from './Popover';
import type { PrimitiveComponentContracts } from './primitiveComponentContracts';
import { RestoreResourceRecord } from './RestoreResourceRecord';
import { ShowResourceRecord } from './ShowResourceRecord';
import { SimpleTable } from './SimpleTable';
import { Slideover } from './Slideover';
import { StatCard } from './StatCard';
import { Step, Stepper } from './Stepper';
import { Tabs } from './Tabs';
import { ThemeToggle } from './ThemeToggle';
import { Tooltip } from './Tooltip';
import { LogoutButton, UserMenu } from './UserMenu';

const Badge: PrimitiveComponentContracts['Badge'] = UiBadge;
const Button: PrimitiveComponentContracts['Button'] = UiButton;
const SearchBar: PrimitiveComponentContracts['SearchBar'] = UiSearchBar;
const Separator: PrimitiveComponentContracts['Separator'] = UiSeparator;
const Skeleton: PrimitiveComponentContracts['Skeleton'] = UiSkeleton;
const Spinner: PrimitiveComponentContracts['Spinner'] = UiSpinner;

export const preloadAiAgentButton = () => import('./AiAgentButton');
const AiAgentButton = lazyComponent(async () => ({
  default: (await import('./AiAgentButton')).AiAgentButton
}));

const CreateResourceRecordForm = lazyComponent(async () => ({
  default: (await import('./CreateResourceRecordForm')).CreateResourceRecordForm
}));

const RecordFields = lazyComponent(async () => ({
  default: (await import('./RecordFields')).RecordFields
}));

const DataTableFilters = lazyComponent(async () => ({
  default: (await import('./DataTableFilters')).DataTableFilters
}));

const DataTableFilterChips = lazyComponent(async () => ({
  default: (await import('./DataTableFilterChips')).DataTableFilterChips
}));

const DataTableAddFilter = lazyComponent(async () => ({
  default: (await import('./DataTableFilterChips')).DataTableAddFilter
}));

const DataTable = lazyComponent(async () => ({
  default: (await import('./DataTable')).DataTable
}));

const DataTableBulkActions = lazyComponent<PrimitiveComponentContracts['DataTableBulkActions']>(
  async () => ({
    default: (await import('@Pages/pageRuntime/components/ui/data-table/DataTableBulkActions'))
      .DataTableBulkActions
  })
);

const DataTableColumnToggle = lazyComponent<PrimitiveComponentContracts['DataTableColumnToggle']>(
  async () => ({
    default: (await import('./DataTableColumnToggle')).DataTableColumnToggle
  })
);

const DataTableContent = lazyComponent<PrimitiveComponentContracts['DataTableContent']>(
  async () => ({
    default: (await import('@Pages/pageRuntime/components/ui/data-table/DataTableContent'))
      .DataTableContent
  })
);

const DataTablePagination = lazyComponent<PrimitiveComponentContracts['DataTablePagination']>(
  async () => ({
    default: (await import('@Pages/pageRuntime/components/ui/data-table/DataTablePagination'))
      .DataTablePagination
  })
);

const SharedFilters = lazyComponent(async () => ({
  default: (await import('./SharedFilters')).SharedFilters
}));

const DataTableRowActions = lazyComponent<PrimitiveComponentContracts['DataTableRowActions']>(
  async () => ({
    default: (await import('@Pages/pageRuntime/components/ui/data-table/DataTableRowActions'))
      .DataTableRowActions
  })
);

const DataTableSearch = lazyComponent<PrimitiveComponentContracts['DataTableSearch']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/data-table/DataTableSearch'))
    .DataTableSearch
}));

const DataTableToolbar = lazyComponent<PrimitiveComponentContracts['DataTableToolbar']>(
  async () => ({
    default: (await import('@Pages/pageRuntime/components/ui/data-table/DataTableToolbar'))
      .DataTableToolbar as PrimitiveComponentContracts['DataTableToolbar']
  })
);

const PackageReleaseNotes = lazyComponent(async () => ({
  default: (await import('./PackageReleaseNotes')).PackageReleaseNotes
}));

const PaymentForm = lazyComponent(async () => ({
  default: (await import('./PaymentForm')).PaymentForm
}));

const PaymentMethods = lazyComponent(async () => ({
  default: (await import('./PaymentMethods')).PaymentMethods
}));

const SubscriptionBillingPreview = lazyComponent(async () => ({
  default: (await import('./SubscriptionBillingPreview')).SubscriptionBillingPreview
}));

const SlotPicker = lazyComponent(async () => ({
  default: (await import('./SlotPicker')).SlotPicker
}));

const SubscriptionChangePreview = lazyComponent(async () => ({
  default: (await import('./SubscriptionChangePreview')).SubscriptionChangePreview
}));

const RecordScheduleDisplay = lazyComponent(async () => ({
  default: (await import('./RecordScheduleDisplay')).RecordScheduleDisplay
}));

const QrCode = lazyComponent(async () => ({
  default: (await import('./QrCode')).QrCode
}));

function modulePreloader<TModule>(load: () => Promise<TModule>) {
  let loaded = false;
  let loading: Promise<TModule> | null = null;

  return {
    isLoaded: () => loaded,
    preload: () =>
      (loading ??= load().then((module) => {
        loaded = true;
        return module;
      }))
  };
}

const calendarViewPreloader = modulePreloader(() => import('./calendar/CalendarView'));
const Calendar = lazyComponent<PrimitiveComponentContracts['Calendar']>(async () => ({
  default: (await calendarViewPreloader.preload())
    .CalendarView as PrimitiveComponentContracts['Calendar']
}));

const componentLoaders = [
  {
    pattern: /\bCalendar\b/,
    loader: calendarViewPreloader
  },
  {
    pattern:
      /\b(?:CalendarNextButton|CalendarPrevButton|CalendarTitle|CalendarTodayButton|CalendarViewSwitcher)\b/,
    loader: modulePreloader(() => import('./calendar/CalendarHeaderComponents'))
  },
  {
    pattern: /\bCalendarSelector\b/,
    loader: modulePreloader(() => import('./calendar/CalendarSelector'))
  },
  {
    pattern: /\bDataTable\b/,
    loader: modulePreloader(() => import('./DataTable'))
  },
  {
    pattern: /\bResourceTable\b/,
    loader: modulePreloader(() => import('./ResourceTable'))
  },
  {
    pattern: /\bCreateResourceRecordForm\b/,
    loader: modulePreloader(() => import('./CreateResourceRecordForm'))
  },
  {
    pattern: /\bUpdateResourceRecordForm\b/,
    loader: modulePreloader(() => import('./UpdateResourceRecordForm'))
  },
  {
    pattern: /\bWorkflowForm\b/,
    loader: modulePreloader(() => import('./WorkflowForm'))
  },
  {
    pattern: /\bWorkspaceFieldsForm\b/,
    loader: modulePreloader(() => import('./WorkspaceFieldsForm'))
  }
];

export function prepareLazyComponentsForSource(
  sources: Array<string | null | undefined>
): Promise<unknown> | null {
  const loading = componentLoaders
    .filter(
      ({ pattern, loader }) =>
        !loader.isLoaded() && sources.some((source) => source && pattern.test(source))
    )
    .map(({ loader }) => loader.preload());

  return loading.length > 0 ? Promise.all(loading) : null;
}

const CalendarNextButton = lazyComponent(async () => ({
  default: (await import('./calendar/CalendarHeaderComponents')).CalendarNextButton
}));

const CalendarPrevButton = lazyComponent(async () => ({
  default: (await import('./calendar/CalendarHeaderComponents')).CalendarPrevButton
}));

const CalendarSelector = lazyComponent(async () => ({
  default: (await import('./calendar/CalendarSelector')).CalendarSelector
}));

const CalendarTitle = lazyComponent(async () => ({
  default: (await import('./calendar/CalendarHeaderComponents')).CalendarTitle
}));

const CalendarTodayButton = lazyComponent(async () => ({
  default: (await import('./calendar/CalendarHeaderComponents')).CalendarTodayButton
}));

const CalendarViewSwitcher = lazyComponent(async () => ({
  default: (await import('./calendar/CalendarHeaderComponents')).CalendarViewSwitcher
}));

const Iframe = lazyComponent(async () => ({
  default: (await import('./Display')).Iframe
}));

const Image = lazyComponent(async () => ({
  default: (await import('./Image')).Image
}));

const Map = lazyComponent(async () => ({
  default: (await import('./Display')).Map
}));

const RichText = lazyComponent(async () => ({
  default: (await import('./Display')).RichText
}));

const Timer = lazyComponent(async () => ({
  default: (await import('./Display')).Timer
}));

const Video = lazyComponent(async () => ({
  default: (await import('./Display')).Video
}));

const EmailMessages = lazyComponent(async () => ({
  default: (await import('./EmailMessages')).EmailMessages
}));

const NotificationComposer = lazyComponent(async () => ({
  default: (await import('./NotificationComposer')).NotificationComposer
}));

const ScheduledNotificationComposer = lazyComponent(async () => ({
  default: (await import('./ScheduledNotificationComposer')).ScheduledNotificationComposer
}));

const CalendarConnections = lazyComponent(async () => ({
  default: (await import('./CalendarConnections')).CalendarConnections
}));

const NotificationConsentForm = lazyComponent(async () => ({
  default: (await import('./NotificationConsentForm')).NotificationConsentForm
}));

const TwoFactorChallenge = lazyComponent(async () => ({
  default: (await import('./TwoFactorChallenge')).TwoFactorChallenge
}));

const TwoFactorSetup = lazyComponent(async () => ({
  default: (await import('./TwoFactorSetup')).TwoFactorSetup
}));

const TwoFactorBackupCodes = lazyComponent(async () => ({
  default: (await import('./TwoFactorBackupCodes')).TwoFactorBackupCodes
}));

const TwoFactorSmsConsent = lazyComponent(async () => ({
  default: (await import('./TwoFactorSendControls')).TwoFactorSmsConsent
}));

const TwoFactorSendCodeButton = lazyComponent(async () => ({
  default: (await import('./TwoFactorSendControls')).TwoFactorSendCodeButton
}));

const TwoFactorBackupCodeList = lazyComponent(async () => ({
  default: (await import('./TwoFactorSendControls')).TwoFactorBackupCodeList
}));

const NotificationHistory = lazyComponent(async () => ({
  default: (await import('./NotificationHistory')).NotificationHistory
}));

const NotificationVariablesForm = lazyComponent(async () => ({
  default: (await import('./NotificationVariablesForm')).NotificationVariablesForm
}));

const EmailComposer = lazyComponent(async () => ({
  default: (await import('./EmailComposer')).EmailComposer
}));

const FormContentInput = lazyComponent(async () => ({
  default: (await import('./forms/ContentInput')).FormContentInput
}));

const FormCheckbox = lazyComponent(async () => ({
  default: (await import('./forms/Checkbox')).FormCheckbox
}));

const FormCheckboxGroupInput = lazyComponent(async () => ({
  default: (await import('./forms/CheckboxGroupInput')).FormCheckboxGroupInput
}));

const FormCodeInput = lazyComponent(async () => ({
  default: (await import('./forms/CodeInput')).FormCodeInput
}));

const FormColorInput = lazyComponent<PrimitiveComponentContracts['FormColorInput']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/form/FormColorInput'))
    .FormColorInput as PrimitiveComponentContracts['FormColorInput']
}));

const FormComboboxInput = lazyComponent(async () => ({
  default: (await import('./forms/ComboboxInput')).FormComboboxInput
}));

const FormDateInput = lazyComponent<PrimitiveComponentContracts['FormDateInput']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/form/FormDateInput'))
    .FormDateInput as PrimitiveComponentContracts['FormDateInput']
}));

const FormDatetimeInput = lazyComponent<PrimitiveComponentContracts['FormDatetimeInput']>(
  async () => ({
    default: (await import('@Pages/pageRuntime/components/ui/form/FormDatetimeInput'))
      .FormDatetimeInput as PrimitiveComponentContracts['FormDatetimeInput']
  })
);

const FormFieldInput = lazyComponent(async () => ({
  default: (await import('./forms/FieldInput')).FormFieldInput
}));

const FormFieldDisplay = lazyComponent(async () => ({
  default: (await import('./forms/FieldDisplay')).FormFieldDisplay
}));

const FormFileUpload = lazyComponent<PrimitiveComponentContracts['FormFileUpload']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/form/FormFileInput'))
    .FormFileInput as PrimitiveComponentContracts['FormFileUpload']
}));

const FormLocationInput = lazyComponent<PrimitiveComponentContracts['FormLocationInput']>(
  async () => ({
    default: (await import('@Pages/pageRuntime/components/ui/form/FormLocationInput'))
      .FormLocationInput as PrimitiveComponentContracts['FormLocationInput']
  })
);

const FormMarkdownInput = lazyComponent(async () => ({
  default: (await import('./forms/MarkdownInput')).FormMarkdownInput
}));

const FormMoneyInput = lazyComponent<PrimitiveComponentContracts['FormMoneyInput']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/form/FormMoneyInput'))
    .FormMoneyInput as PrimitiveComponentContracts['FormMoneyInput']
}));

const FormMultiComboboxInput = lazyComponent(async () => ({
  default: (await import('./forms/MultiComboboxInput')).FormMultiComboboxInput
}));

const FormNumberInput = lazyComponent(async () => ({
  default: (await import('./forms/NumberInput')).FormNumberInput
}));

const FormPasswordInput = lazyComponent<PrimitiveComponentContracts['FormPasswordInput']>(
  async () => ({
    default: (await import('@Pages/pageRuntime/components/ui/form/FormPasswordInput'))
      .FormPasswordInput as PrimitiveComponentContracts['FormPasswordInput']
  })
);

const FormPhoneInput = lazyComponent(async () => ({
  default: (await import('./forms/PhoneInput')).FormPhoneInput
}));

const FormRadio = lazyComponent(async () => ({
  default: (await import('./forms/Radio')).FormRadio
}));

const FormSelectInput = lazyComponent(async () => ({
  default: (await import('./forms/SelectInput')).FormSelectInput
}));

const FormSlider = lazyComponent(async () => ({
  default: (await import('./forms/Slider')).FormSlider
}));

const FormSwitch = lazyComponent(async () => ({
  default: (await import('./forms/Switch')).FormSwitch
}));

const FormTextArea = lazyComponent<PrimitiveComponentContracts['FormTextArea']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/form/FormTextarea'))
    .FormTextarea as PrimitiveComponentContracts['FormTextArea']
}));

const FormTextInput = lazyComponent<PrimitiveComponentContracts['FormTextInput']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/form/FormTextInput'))
    .FormTextInput as PrimitiveComponentContracts['FormTextInput']
}));

const FormTimeInput = lazyComponent<PrimitiveComponentContracts['FormTimeInput']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/form/FormTimeInput'))
    .FormTimeInput as PrimitiveComponentContracts['FormTimeInput']
}));

const FormTimezoneInput = lazyComponent(async () => ({
  default: (await import('./forms/TimezoneInput')).FormTimezoneInput
}));

const NavMain = lazyComponent<PrimitiveComponentContracts['NavMain']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/NavMain')).NavMain
}));

const ResourceTable = lazyComponent(async () => ({
  default: (await import('./ResourceTable')).ResourceTable
}));

const Sidebar = lazyComponent<PrimitiveComponentContracts['Sidebar']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/Sidebar')).Sidebar
}));

const SidebarCollapseButton = lazyComponent(async () => ({
  default: (await import('./Sidebar')).SidebarCollapseButton
}));

const SidebarContent = lazyComponent<PrimitiveComponentContracts['SidebarContent']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/Sidebar')).SidebarContent
}));

const SidebarFooter = lazyComponent<PrimitiveComponentContracts['SidebarFooter']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/Sidebar')).SidebarFooter
}));

const SidebarHeader = lazyComponent<PrimitiveComponentContracts['SidebarHeader']>(async () => ({
  default: (await import('./Sidebar')).SidebarHeader
}));

const SidebarInset = lazyComponent<PrimitiveComponentContracts['SidebarInset']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/Sidebar')).SidebarInset
}));

const SidebarMenu = lazyComponent<PrimitiveComponentContracts['SidebarMenu']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/Sidebar')).SidebarMenu
}));

const SidebarMenuButton = lazyComponent<PrimitiveComponentContracts['SidebarMenuButton']>(
  async () => ({
    default: (await import('@Pages/pageRuntime/components/ui/Sidebar')).SidebarMenuButton
  })
);

const SidebarMenuItem = lazyComponent<PrimitiveComponentContracts['SidebarMenuItem']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/Sidebar')).SidebarMenuItem
}));

const SidebarProvider = lazyComponent<PrimitiveComponentContracts['SidebarProvider']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/Sidebar')).SidebarProvider
}));

const SidebarTrigger = lazyComponent<PrimitiveComponentContracts['SidebarTrigger']>(async () => ({
  default: (await import('@Pages/pageRuntime/components/ui/Sidebar')).SidebarTrigger
}));

const SupportChatButton = lazyComponent(async () => ({
  default: (await import('./SupportChatButton')).SupportChatButton
}));

const UpdateResourceRecordForm = lazyComponent(async () => ({
  default: (await import('./UpdateResourceRecordForm')).UpdateResourceRecordForm
}));

const WorkflowForm = lazyComponent(async () => ({
  default: (await import('./WorkflowForm')).WorkflowForm
}));

const WorkspaceFieldsForm = lazyComponent(async () => ({
  default: (await import('./WorkspaceFieldsForm')).WorkspaceFieldsForm
}));

export const componentRegistry = {
  Accordion,
  AdminNavButtons,
  AiAgentButton,
  Alert,
  AnalyticsEmbed,
  Avatar,
  Badge,
  BarChart,
  Breadcrumb,
  Button,
  ButtonLink,
  Calendar,
  CalendarConnections,
  CalendarNextButton,
  CalendarPrevButton,
  CalendarSelector,
  CalendarTitle,
  CalendarTodayButton,
  CalendarViewSwitcher,
  Card,
  Collapsible,
  ConfirmDialog,
  CreateResourceRecordForm,
  DataTable,
  DataTableAddFilter,
  DataTableBulkActions,
  DataTableColumnToggle,
  DataTableContent,
  DataTableFilterChips,
  DataTableFilters,
  DataTablePagination,
  DataTableRowActions,
  DataTableSearch,
  DataTableToolbar,
  DeleteResourceRecord,
  DropdownMenu,
  EmailComposer,
  EmailMessages,
  EmptyState,
  ErrorBoundary,
  ForgotPasswordForm,
  FormFieldDisplay,
  FormFieldInput,
  FormCheckbox,
  FormCheckboxGroupInput,
  FormCodeInput,
  FormColorInput,
  FormComboboxInput,
  FormContentInput,
  FormDateInput,
  FormDatetimeInput,
  FormFileUpload,
  FormLocationInput,
  FormMarkdownInput,
  FormMoneyInput,
  FormMultiComboboxInput,
  FormNumberInput,
  FormPasswordInput,
  FormPhoneInput,
  FormRadio,
  FormSelectInput,
  FormSlider,
  FormSwitch,
  FormTextArea,
  FormTextInput,
  FormTimeInput,
  FormTimezoneInput,
  HasAnyPermission,
  HasPermission,
  Icon,
  Iframe,
  Image,
  KeyValueList,
  Link,
  LineChart,
  List,
  LoginForm,
  LogoutButton,
  Map,
  Modal,
  NavMain,
  NotificationComposer,
  NotificationConsentForm,
  NotificationHistory,
  NotificationVariablesForm,
  PackageReleaseNotes,
  PageHeader,
  PaymentForm,
  PaymentMethods,
  PieChart,
  Popover,
  QrCode,
  RecordFields,
  RecordScheduleDisplay,
  ResourceTable,
  RestoreResourceRecord,
  RichText,
  ScheduledNotificationComposer,
  SearchBar,
  Separator,
  SharedFilters,
  ShowResourceRecord,
  Sidebar,
  SidebarCollapseButton,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SimpleTable,
  Skeleton,
  Slideover,
  SlotPicker,
  Spinner,
  StatCard,
  Step,
  Stepper,
  SubscriptionBillingPreview,
  SubscriptionChangePreview,
  SupportChatButton,
  Tabs,
  ThemeToggle,
  Timer,
  Tooltip,
  TwoFactorBackupCodeList,
  TwoFactorBackupCodes,
  TwoFactorChallenge,
  TwoFactorSendCodeButton,
  TwoFactorSetup,
  TwoFactorSmsConsent,
  UpdateResourceRecordForm,
  UserMenu,
  Video,
  WorkflowForm,
  WorkspaceFieldsForm
};

export type ComponentRegistry = typeof componentRegistry;
